/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { IdentificationDocumentTypeEnum } from './IdentificationDocumentTypeEnum';
import {
    IdentificationDocumentTypeEnumFromJSON,
    IdentificationDocumentTypeEnumFromJSONTyped,
    IdentificationDocumentTypeEnumToJSON,
} from './IdentificationDocumentTypeEnum';
import type { IdentificationStatusEnum } from './IdentificationStatusEnum';
import {
    IdentificationStatusEnumFromJSON,
    IdentificationStatusEnumFromJSONTyped,
    IdentificationStatusEnumToJSON,
} from './IdentificationStatusEnum';
import type { IdentificationMethodEnum } from './IdentificationMethodEnum';
import {
    IdentificationMethodEnumFromJSON,
    IdentificationMethodEnumFromJSONTyped,
    IdentificationMethodEnumToJSON,
} from './IdentificationMethodEnum';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
} from './Document';
import type { IdentityProviderEnum } from './IdentityProviderEnum';
import {
    IdentityProviderEnumFromJSON,
    IdentityProviderEnumFromJSONTyped,
    IdentityProviderEnumToJSON,
} from './IdentityProviderEnum';

/**
 * 
 * @export
 * @interface Identification
 */
export interface Identification {
    /**
     * 
     * @type {string}
     * @memberof Identification
     */
    readonly id: string;
    /**
     * 
     * @type {string}
     * @memberof Identification
     */
    personId?: string;
    /**
     * Type of the document used for the identification.
     * @type {IdentificationDocumentTypeEnum}
     * @memberof Identification
     */
    documentType?: IdentificationDocumentTypeEnum;
    /**
     * 
     * @type {string}
     * @memberof Identification
     */
    documentId?: string;
    /**
     * The office which issued the document, such as Bürgerbüro XY, corresponds to authority in postident service response
     * @type {string}
     * @memberof Identification
     */
    documentIssuer?: string;
    /**
     * The first point in time when the identification document is valid. On German identity cards this is the date of issuance.
     * @type {Date}
     * @memberof Identification
     */
    documentValidFrom?: Date | null;
    /**
     * 
     * @type {Date}
     * @memberof Identification
     */
    documentValidTo: Date | null;
    /**
     * 
     * @type {IdentityProviderEnum}
     * @memberof Identification
     */
    identityProvider?: IdentityProviderEnum;
    /**
     * 
     * @type {string}
     * @memberof Identification
     */
    legitimationProcessId?: string;
    /**
     * Document of identification protocol as PDF
     * @type {Document}
     * @memberof Identification
     */
    readonly legitimationProtocol: Document;
    /**
     * Date and time on which the legitimation has been verified by the identity provider.
     * @type {Date}
     * @memberof Identification
     */
    verifiedAt: Date | null;
    /**
     * The method to be used for the identification.
     * @type {IdentificationMethodEnum}
     * @memberof Identification
     */
    readonly method: IdentificationMethodEnum;
    /**
     * 
     * @type {IdentificationStatusEnum}
     * @memberof Identification
     */
    readonly status: IdentificationStatusEnum;
    /**
     * 
     * @type {string}
     * @memberof Identification
     */
    readonly url: string;
}



/**
 * Check if a given object implements the Identification interface.
 */
export function instanceOfIdentification(value: object): value is Identification {
    if (!('id' in value) || value['id'] === undefined) return false;
    if (!('documentValidTo' in value) || value['documentValidTo'] === undefined) return false;
    if (!('legitimationProtocol' in value) || value['legitimationProtocol'] === undefined) return false;
    if (!('verifiedAt' in value) || value['verifiedAt'] === undefined) return false;
    if (!('method' in value) || value['method'] === undefined) return false;
    if (!('status' in value) || value['status'] === undefined) return false;
    if (!('url' in value) || value['url'] === undefined) return false;
    return true;
}

export function IdentificationFromJSON(json: any): Identification {
    return IdentificationFromJSONTyped(json, false);
}

export function IdentificationFromJSONTyped(json: any, ignoreDiscriminator: boolean): Identification {
    if (json == null) {
        return json;
    }
    return {
        
        'id': json['id'],
        'personId': json['person_id'] == null ? undefined : json['person_id'],
        'documentType': json['document_type'] == null ? undefined : IdentificationDocumentTypeEnumFromJSON(json['document_type']),
        'documentId': json['document_id'] == null ? undefined : json['document_id'],
        'documentIssuer': json['document_issuer'] == null ? undefined : json['document_issuer'],
        'documentValidFrom': json['document_valid_from'] == null ? undefined : (new Date(json['document_valid_from'])),
        'documentValidTo': (json['document_valid_to'] == null ? null : new Date(json['document_valid_to'])),
        'identityProvider': json['identity_provider'] == null ? undefined : IdentityProviderEnumFromJSON(json['identity_provider']),
        'legitimationProcessId': json['legitimation_process_id'] == null ? undefined : json['legitimation_process_id'],
        'legitimationProtocol': DocumentFromJSON(json['legitimation_protocol']),
        'verifiedAt': (json['verified_at'] == null ? null : new Date(json['verified_at'])),
        'method': IdentificationMethodEnumFromJSON(json['method']),
        'status': IdentificationStatusEnumFromJSON(json['status']),
        'url': json['url'],
    };
}

export function IdentificationToJSON(value?: Omit<Identification, 'id'|'legitimation_protocol'|'method'|'status'|'url'> | null): any {
    if (value == null) {
        return value;
    }
    return {
        
        'person_id': value['personId'],
        'document_type': IdentificationDocumentTypeEnumToJSON(value['documentType']),
        'document_id': value['documentId'],
        'document_issuer': value['documentIssuer'],
        'document_valid_from': value['documentValidFrom'] == null ? undefined : ((value['documentValidFrom'] as any).toISOString().substring(0,10)),
        'document_valid_to': (value['documentValidTo'] == null ? null : (value['documentValidTo'] as any).toISOString().substring(0,10)),
        'identity_provider': IdentityProviderEnumToJSON(value['identityProvider']),
        'legitimation_process_id': value['legitimationProcessId'],
        'verified_at': (value['verifiedAt'] == null ? null : (value['verifiedAt'] as any).toISOString()),
    };
}

